/** biome-ignore-all lint/performance/noNamespaceImport: ok */
"use client";
import { Fieldset, Form } from "@base-ui/react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { Info } from "lucide-react";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import image from "#/assets/images/calc-percentile.svg";
import PageHeader from "@/components/layout/page-header";
import { Button } from "@/components/ui/button";
import ExtendedImage from "@/components/ui/extended-image";
import * as Field from "@/components/ui/form/field";
import * as Radio from "@/components/ui/form/radio";
import { RadioGroup } from "@/components/ui/form/radio-group";
import { CalculateIdealWeightAction } from "../_components/action";
import {
  type IdealWeightModel,
  type IdealWeightResponse,
  IdealWeightSchema,
} from "../_components/types";
import { ResultDialog } from "./_components/result-dialog";

const PercentileCalculation = () => {
  const { control, handleSubmit } = useForm<IdealWeightModel>({
    defaultValues: { birthDate: "", gender: "1", heightCm: "", weightGram: "" },
    resolver: zodResolver(IdealWeightSchema),
  });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [result, setResult] = useState<IdealWeightResponse | null>(null);

  const mutation = useMutation({
    mutationFn: async (data: IdealWeightModel) => {
      return await CalculateIdealWeightAction(data);
    },
    onSuccess: (resp) => {
      if (resp.isSuccess) {
        setResult(resp.result);
        setDialogOpen(true);
      }
    },
  });

  const onSubmit = (data: IdealWeightModel) => {
    mutation.mutate(data);
  };

  return (
    <div className="flex flex-col gap-4 justify-center mb-20">
      <PageHeader title="Persentil Hesaplama" />
      <div className="rounded-full border border-gray-300 size-56 mx-auto mb-10">
        <ExtendedImage
          alt="ovum"
          className="rounded-full size-56 "
          height={224}
          src={image}
          width={224}
        />
      </div>
      <Form
        className="w-96 mx-auto flex flex-col gap-4"
        onSubmit={handleSubmit(onSubmit)}
      >
        <Controller
          control={control}
          name="gender"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Cinsiyet</Field.Label>
                <Fieldset.Root
                  render={
                    <RadioGroup
                      className="gap-4"
                      inputRef={field.ref}
                      onValueChange={field.onChange}
                      value={field.value}
                    />
                  }
                >
                  <Field.Item>
                    <Field.Label className="flex flex-row gap-2 font-semibold">
                      <Radio.Root onBlur={field.onBlur} value="2">
                        <Radio.Indicator />
                      </Radio.Root>
                      Kız
                    </Field.Label>
                  </Field.Item>
                  <Field.Item>
                    <Field.Label className="flex flex-row gap-2 font-semibold">
                      <Radio.Root onBlur={field.onBlur} value="1">
                        <Radio.Indicator />
                      </Radio.Root>
                      Erkek
                    </Field.Label>
                  </Field.Item>
                </Fieldset.Root>
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <Controller
          control={control}
          name="birthDate"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Bebeğin Doğum Tarihi</Field.Label>
                <Field.Control
                  onBlur={field.onBlur}
                  onValueChange={field.onChange}
                  ref={field.ref}
                  type="date"
                  value={field.value}
                />

                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <Controller
          control={control}
          name="heightCm"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Bebeğin Boyu</Field.Label>
                <Field.Control
                  inputMode="numeric"
                  onBlur={field.onBlur}
                  onValueChange={(val) =>
                    field.onChange(String(val).replace(/[^0-9.]/g, ""))
                  }
                  placeholder="30cm"
                  ref={field.ref}
                  type="text"
                  value={field.value}
                />
                <Field.Description className="flex flex-row justify-center items-center gap-2">
                  <Info className="size-4 shrink-0 text-gray-500" />
                  cm cinsinden giriniz
                </Field.Description>
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <Controller
          control={control}
          name="weightGram"
          render={({ field, fieldState }) => {
            return (
              <Field.Root
                dirty={fieldState.isDirty}
                invalid={fieldState.invalid}
                name={field.name}
                touched={fieldState.isTouched}
              >
                <Field.Label>Bebeğin Kilosu</Field.Label>
                <Field.Control
                  inputMode="numeric"
                  onBlur={field.onBlur}
                  onValueChange={(val) =>
                    field.onChange(String(val).replace(/[^0-9.]/g, ""))
                  }
                  placeholder="3200gr"
                  ref={field.ref}
                  type="text"
                  value={field.value}
                />
                <Field.Description className="flex flex-row justify-center items-center gap-2">
                  <Info className="size-4 shrink-0 text-gray-500" />
                  Gram cinsinden giriniz
                </Field.Description>
                <Field.Error match={!!fieldState.error}>
                  {fieldState.error?.message}
                </Field.Error>
              </Field.Root>
            );
          }}
        />
        <div className="flex flex-row gap-2 bg-gray-100 rounded-xl p-2 justify-start items-center w-fit mx-auto mt-4">
          <Info className="size-5 shrink-0 text-gray-500" />
          <p className="text-[10px] leading-3.5 font-medium text-gray-600">
            Bu hesaplamalar genel bilgilendirme amaçlıdır; tıbbi değerlendirme
            yerine geçmez. En doğru yorum için doktorunuza danışınız.
          </p>
        </div>
        <Button disabled={mutation.isPending} type="submit">
          {mutation.isPending ? "Hesaplanıyor..." : "Hesapla"}
        </Button>
      </Form>
      <ResultDialog
        onOpenChange={setDialogOpen}
        open={dialogOpen}
        result={result}
      />
    </div>
  );
};

export default PercentileCalculation;
